import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { IntlProvider } from 'react-intl'
import { DndProvider } from 'react-dnd'
import { DirectionProvider } from '@radix-ui/react-direction'
import MultiBackend from 'react-dnd-multi-backend'
import HTML5toTouch from 'react-dnd-multi-backend/dist/esm/HTML5toTouch'
import { Box } from '@mui/material'
import TourIcon from '@mui/icons-material/Tour'
import NOTIFICATION from '../../../app/data/notification.json'
import MenusContainer from '../menus/MenusContainer'
import StreetNameplateContainer from '../streets/StreetNameplateContainer'
import InfoBubble from '../info_bubble/InfoBubble'
import PaletteContainer from '../palette/PaletteContainer'
import DialogRoot from '../dialogs/DialogRoot'
import EnvironmentEditor from '../streets/EnvironmentEditor'
import Gallery from '../gallery/Gallery'
import SegmentDragLayer from '../segments/SegmentDragLayer'
import DebugHoverPolygon from '../info_bubble/DebugHoverPolygon'
import ToastContainer from '../ui/Toasts/ToastContainer'
import SentimentSurveyContainer from '../sentiment/SentimentSurveyContainer'
import { getInitialFlags } from '../store/slices/flags'
import SubHeader from '../Multimodal/components/Headers/SubHeader'
import WidthAlert from '../Multimodal/components/WidthAlert'
import {
  FabWrapper,
  StyledFab
} from '../Multimodal/components/Mapview/LayerSelector'
import { LightTooltip } from '../Multimodal/components/CriteriaSelection/ModalCriteria'
import { setStartStreetmixTour } from '../Multimodal/slices/tourSlice'
import CopyToClipboardButton from '../Multimodal/components/CriteriaSelection/CopyToClipboardButton'
import DebugInfo from './DebugInfo'
import BlockingShield from './BlockingShield'
import BlockingError from './BlockingError'
import StreetView from './StreetView'
import PrintContainer from './PrintContainer'
import WelcomePanel from './WelcomePanel'
import NotificationBar from './NotificationBar'
import { setStreetSectionTop } from './window_resize'
import Loading from './Loading'
import AmenitiesInfoAlert from '../Multimodal/components/AmenitiesInfoAlert'

function StreetmixApp() {
  const [isLoading, setLoading] = useState(true)
  const locale = useSelector((state) => state.locale)

  const dir = useSelector((state) => state.app.contentDirection)
  const { amenitiesNotSeenOnStreetmix } = useSelector((state) => state.appSlice)
  const { width, occupiedWidth } = useSelector((state) => state.street)
  const everythingLoaded = useSelector((state) => state.app.everythingLoaded)
  const dispatch = useDispatch()

  // TODO: Move other initialization methods here.
  useEffect(() => {
    const init = async () => {
      // Initialize feature flags
      await dispatch(getInitialFlags())

      // Turn off loading after initial loading is done
      setLoading(false)
    }
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // After loading, do ancient DOM stuff
  useEffect(() => {
    if (!isLoading && everythingLoaded) {
      setStreetSectionTop()
    }
  }, [isLoading, everythingLoaded])

  return (
    <>
      <Loading isLoading={isLoading || !everythingLoaded} />
      {/* <NavigationHeader /> */}
      <div
        style={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexGrow: 1
        }}
      >
        {!isLoading && everythingLoaded && (
          <DirectionProvider dir={dir}>
            <IntlProvider
              locale={locale.locale}
              key={locale.locale}
              messages={locale.messages}
            >
              {/* The prop context={window} prevents crash errors with hot-module reloading */}
              <DndProvider
                backend={MultiBackend}
                options={HTML5toTouch}
                context={window}
              >
                {/* DndProvider allows multiple children; IntlProvider does not */}

                <NotificationBar notification={NOTIFICATION} />
                <BlockingShield />
                <BlockingError />
                <Gallery />
                <DialogRoot />
                <DebugInfo />
                <PrintContainer />
                <div className="main-screen">
                  <MenusContainer />
                  <StreetNameplateContainer />
                  <InfoBubble />
                  <DebugHoverPolygon />
                  {/* <WelcomePanel /> */}
                  <PaletteContainer />
                  <EnvironmentEditor />
                  <SegmentDragLayer />
                  <StreetView />
                  <ToastContainer />
                  <SentimentSurveyContainer />
                  <SubHeader />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '115px',
                      left: '15px',
                      display: 'flex'
                    }}
                  >
                    <CopyToClipboardButton />
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '175px',

                      display: 'flex'
                    }}
                  >
                    <FabWrapper top="0px" left="30px">
                      <Box
                        sx={{ width: '58px !important' }}
                        className="tourStreetmix"
                      >
                        <LightTooltip
                          title="Click here to take the tour"
                          placement="right"
                        >
                          <StyledFab
                            color="primary"
                            aria-label="add"
                            onClick={() => {
                              dispatch(setStartStreetmixTour(true))
                            }}
                            disableRipple={true}
                            borderradius="50%"
                          >
                            <TourIcon sx={{ width: 30, height: 30 }} />
                          </StyledFab>
                        </LightTooltip>
                      </Box>
                    </FabWrapper>
                  </Box>
                  {width < occupiedWidth && (
                    <WidthAlert
                      top={amenitiesNotSeenOnStreetmix.length ? 185 : 130}
                    />
                  )}
                  {amenitiesNotSeenOnStreetmix.length && <AmenitiesInfoAlert />}
                </div>
              </DndProvider>
            </IntlProvider>
          </DirectionProvider>
        )}
      </div>
    </>
  )
}

export default StreetmixApp
